import React, { useState, useMemo, useEffect } from "react";
import {
  AlertTriangle, CheckCircle2, Eye, RefreshCw, X, XCircle,
} from "lucide-react";
import { Modal, MODAL_HEADER, MODAL_ACTIONS } from "../shared/Modal";
import * as api from "../../api";
import { useAppContext } from "../../AppContext";
import { REVIEW_REASON_LABEL } from "../../lib/reviewReasons";

/* -----------------------------------------------------------------------------
 * ManualVerifyModal — admin manual override for a NEEDS_REVIEW order.
 *
 * Side-by-side layout: receipt image on the left, two editable inputs on
 * the right (Monto Real Bs + Nº Referencia) prefilled with whatever the AI
 * OCR extracted so the admin only edits what's wrong. Final decision is a
 * dropdown (Aprobar / Rechazar). On save, calls
 * `POST /api/orders/{id}/manual-verify` and updates the order in App state.
 * --------------------------------------------------------------------------- */


export function ManualVerifyModal() {
  const ctx = useAppContext();
  const {
    manualVerifyOrder, setManualVerifyOrder,
    orders, setOrders, showToast, formatBs, paymentMethods,
  } = ctx;

  // The list endpoint sends orders with `light=1` (no verificationLog) to
  // keep polling payloads small. Lazy-load the full order the first time
  // the modal opens for it so `extracted` is populated.
  const [fullOrder, setFullOrder] = useState(manualVerifyOrder);
  useEffect(() => {
    if (!manualVerifyOrder) { setFullOrder(null); return; }
    setFullOrder(manualVerifyOrder);
    if (!manualVerifyOrder.verificationLog) {
      api.getOrder(manualVerifyOrder.id)
        .then((full) => setFullOrder(full))
        .catch((e) => console.warn("Manual-verify: lazy full-order fetch failed:", e));
    }
  }, [manualVerifyOrder]);

  const order = fullOrder || manualVerifyOrder;
  // Pago adicional (completar pago parcial) pendiente: se corrige ESE comprobante.
  const topupIdx = useMemo(() => {
    const list = order?.additionalPayments || [];
    for (let i = list.length - 1; i >= 0; i -= 1) if (list[i]?.verificationStatus === "NEEDS_REVIEW") return i;
    return -1;
  }, [order]);
  const topup = topupIdx >= 0 ? order.additionalPayments[topupIdx] : null;
  const paymentMethod = useMemo(
    () => (paymentMethods || []).find((method) => method.id === order?.paymentMethodId),
    [order?.paymentMethodId, paymentMethods],
  );
  // Sólo órdenes creadas con el snapshot nativo USD entran en este flujo.
  // Los pedidos históricos y top-ups mantienen su comportamiento en Bs.
  const isUsdOrder = !topup && order?.paymentCurrency === "USD"
    && (paymentMethod?.type === "USD_PLATFORM" || !paymentMethod);
  const amountUnit = isUsdOrder ? "USD/USDT" : "Bs";
  const extracted = useMemo(
    () => (topup ? topup.verificationLog?.extracted : order?.verificationLog?.extracted) || {},
    [order, topup],
  );
  const initialAmount = useMemo(() => {
    const m = extracted?.monto;
    if (m !== null && m !== undefined && !isNaN(Number(m))) return String(m);
    // Fallback: the amount the user declared at checkout
    const declared = isUsdOrder ? order?.paidAmountUsd : (topup ? topup.paidAmountBs : order?.paidAmountBs);
    if (declared) return String(declared);
    return "";
  }, [order, extracted, topup, isUsdOrder]);
  const initialRef = useMemo(() => {
    const r = extracted?.referencia || (topup ? (topup.referenceNumber || topup.aiReference) : order?.referenceNumber) || "";
    return String(r || "").replace(/\D/g, "");
  }, [order, extracted, topup]);

  const [realAmount, setRealAmount] = useState(initialAmount);
  const [refNumber, setRefNumber] = useState(initialRef);
  const [decision, setDecision] = useState("APPROVE");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgError, setImgError] = useState(false);
  // Inline error banner — persists until the admin edits a field or closes
  // the modal. We used to rely on the 4.5s toast, but admins were missing
  // it and thought the button was broken. Now the modal itself surfaces
  // whatever the backend rejected (409 duplicate ref, 400 validation, etc).
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setRealAmount(initialAmount);
    setRefNumber(initialRef);
    setDecision("APPROVE");
    setNote("");
    setImgError(false);
    setSaveError(null);
  }, [order?.id, initialAmount, initialRef]);

  if (!order) return null;

  const declaredAmount = Number((isUsdOrder ? order.paidAmountUsd : (topup ? topup.paidAmountBs : order.paidAmountBs)) || 0);
  const amountAsNumber = Number(String(realAmount).replace(",", "."));
  const amountsMatchDeclared =
    !isNaN(amountAsNumber) &&
    Math.abs(amountAsNumber - declaredAmount) <= 0.5;

  const activeProofUrl = topup ? topup.proofUrl : order.proofUrl;
  const proofUrl = activeProofUrl
    ? `${process.env.REACT_APP_BACKEND_URL}${activeProofUrl}`
    : null;

  const handleSave = async () => {
    setSaveError(null);
    const parsed = Number(String(realAmount).replace(",", "."));
    if (isNaN(parsed) || parsed < 0) {
      setSaveError(`Ingresa un monto válido en ${amountUnit} (número, sin letras).`);
      return;
    }
    const cleanRef = String(refNumber || "").replace(/\D/g, "");
    if (!cleanRef) {
      setSaveError("Escribe la referencia (solo dígitos).");
      return;
    }
    if (decision === "REJECT" && note.trim().length < 5) {
      setSaveError("Escribe un motivo del rechazo (mínimo 5 caracteres).");
      return;
    }
    setBusy(true);
    try {
      const updated = await api.manualVerifyPayment(order.id, {
        ...(isUsdOrder ? { real_amount_usd: parsed } : { real_amount_bs: parsed }),
        reference_number: cleanRef,
        decision,
        note: note.trim() || null,
      });
      if (typeof setOrders === "function") {
        setOrders(orders?.map((o) => (o.id === updated.id ? updated : o)));
      }
      if (decision === "APPROVE") {
        const falta = Number(updated?.shortageBs || 0);
        showToast("Corrección Guardada",
          falta > 0
            ? `Monto real ${amountUnit} ${parsed.toFixed(2)} guardado · aún faltan Bs ${Math.round(falta)} (Pago Incompleto).`
            : `Monto real ${amountUnit} ${parsed.toFixed(2)} guardado · pago cubierto, pedido ${updated?.status || "aprobado"}.`,
          "success");
      } else {
        showToast("Pedido Rechazado", "Se rechazó manualmente y se reembolsó el saldo aplicado.", "success");
      }
      setManualVerifyOrder(null);
    } catch (err) {
      // Surface the exact backend detail (409 dup ref, 400 validation, 403
      // auth, network, etc.) inline so the admin can't miss it.
      const msg = err?.data?.detail || err?.message || "No se pudo guardar la corrección.";
      setSaveError(String(msg));
      showToast("Error", String(msg), "error");
    } finally {
      setBusy(false);
    }
  };

  const reasonLabel = order.review_reason ? REVIEW_REASON_LABEL[order.review_reason] || order.review_reason : null;

  return (
    <Modal
      onClose={busy ? undefined : () => setManualVerifyOrder(null)}
      size="xl"
      testId="manual-verify-modal"
    >
      {/* Header */}
      <div className={`${MODAL_HEADER} items-start`}>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5 text-amber-300" />
            <h2 className="text-lg sm:text-xl font-black text-white tracking-tight">
              Corrección Manual del Comprobante
            </h2>
          </div>
          <p className="text-xs text-zinc-400 font-mono">
            Pedido{" "}
            <span className="text-cyan-300">
              #{String(order.orderNumber || "").padStart(4, "0") || order.id.slice(0, 6)}
            </span>{" "}
            · Sobreescribe los valores leídos por la IA con los reales del comprobante.
          </p>
        </div>
        <button
          onClick={() => setManualVerifyOrder(null)}
          data-testid="manual-verify-close"
          disabled={busy}
          className="text-zinc-500 hover:text-white disabled:opacity-30"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Reason chip */}
      {reasonLabel && (
        <div
          data-testid="manual-verify-reason"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/50 text-amber-200 text-[11px] font-mono uppercase tracking-widest"
        >
          <AlertTriangle className="w-3 h-3" />
          {reasonLabel}
        </div>
      )}

      {/* Inline error banner — persists so the admin can't miss why the
          save was rejected (duplicate reference, validation, auth, …). */}
      {saveError && (
        <div
          data-testid="manual-verify-error"
          className="flex items-start gap-3 p-3 rounded-xl bg-red-950/40 border border-red-500/60 shadow-[0_0_18px_-6px_rgba(239,68,68,0.7)]"
        >
          <XCircle className="w-4 h-4 text-red-300 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-red-300 mb-0.5">
              No se pudo guardar
            </p>
            <p className="text-xs text-red-100 leading-relaxed">{saveError}</p>
          </div>
          <button
            onClick={() => setSaveError(null)}
            data-testid="manual-verify-error-dismiss"
            className="text-red-300 hover:text-white flex-shrink-0"
            aria-label="Cerrar aviso"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Two-column layout: image + inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {/* LEFT — receipt image */}
        <div className="rounded-2xl border border-zinc-800 bg-[#06070b] p-3 space-y-2">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <Eye className="w-3.5 h-3.5" /> {topup ? `Comprobante · Pago #${topupIdx + 2} (adicional)` : "Comprobante · Pago #1"}
          </div>
          {topup && (
            <p className="text-[11px] text-amber-200 bg-amber-950/30 border border-amber-500/40 rounded-lg p-2" data-testid="manual-verify-topup-note">
              Estás corrigiendo el pago adicional. La referencia del Pago #1
              (<span className="font-mono">{order.referenceNumber || order.ai_reference || "—"}</span>) se conserva intacta.
            </p>
          )}
          {proofUrl && !imgError ? (
            <a
              href={proofUrl}
              target="_blank"
              rel="noreferrer"
              className="block"
              data-testid="manual-verify-image-link"
            >
              <img
                src={proofUrl}
                alt="Comprobante"
                onError={() => setImgError(true)}
                className="w-full max-h-[420px] object-contain rounded-xl border border-zinc-800 bg-black"
                data-testid="manual-verify-image"
              />
            </a>
          ) : (
            <div className="w-full aspect-[3/4] rounded-xl border border-zinc-800 bg-black/60 flex items-center justify-center text-xs text-zinc-500 font-mono">
              Sin imagen de comprobante
            </div>
          )}

          {/* AI extracted echo — for context */}
          <div className="pt-2 border-t border-zinc-800 space-y-1.5 text-[11px] font-mono">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">IA leyó Monto:</span>
              <span className="text-zinc-300">
                {extracted?.monto != null ? `${amountUnit} ${Number(extracted.monto).toFixed(2)}` : "—"}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">IA leyó Ref:</span>
              <span className="text-zinc-300">{extracted?.referencia || "—"}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">Cliente declaró:</span>
              <span className="text-cyan-300">{amountUnit} {declaredAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* RIGHT — editable inputs */}
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5 block">
              <span data-testid="manual-verify-amount-label">Monto Real ({amountUnit})</span> <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={realAmount}
              onChange={(e) => setRealAmount(e.target.value)}
              data-testid="manual-verify-amount-input"
              disabled={busy}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-zinc-700 focus:border-purple-400 text-white text-lg font-mono outline-none transition-colors"
              placeholder={isUsdOrder ? "Ej: 20.00" : "Ej: 6734.00"}
            />
            {!isNaN(amountAsNumber) && (
              <p className={`mt-1.5 text-[11px] font-mono ${amountsMatchDeclared ? "text-emerald-300" : "text-amber-300"}`}>
                {amountsMatchDeclared
                  ? "✓ Coincide con lo declarado por el cliente"
                  : `⚠ Difiere de lo declarado (${amountUnit} ${declaredAmount.toFixed(2)}) por ${amountUnit} ${(amountAsNumber - declaredAmount).toFixed(2)}`}
              </p>
            )}
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5 block">
              Nº Referencia <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={refNumber}
              onChange={(e) => setRefNumber(e.target.value.replace(/\D/g, ""))}
              data-testid="manual-verify-reference-input"
              disabled={busy}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-zinc-700 focus:border-purple-400 text-white text-lg font-mono outline-none transition-colors"
              placeholder="Solo dígitos"
            />
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5 block">
              Decisión Final <span className="text-red-400">*</span>
            </label>
            <select
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              data-testid="manual-verify-decision-select"
              disabled={busy}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-zinc-700 focus:border-purple-400 text-white text-sm font-mono outline-none"
            >
              <option value="APPROVE">✓ Aprobar Pago (procesar orden)</option>
              <option value="REJECT">✗ Rechazar Pago (reembolsar saldo aplicado)</option>
            </select>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1.5 block">
              Nota interna {decision === "REJECT" && <span className="text-red-400">*</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="manual-verify-note-input"
              disabled={busy}
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-black/60 border border-zinc-700 focus:border-purple-400 text-white text-xs font-mono outline-none resize-none"
              placeholder={decision === "REJECT" ? "Motivo del rechazo (visible al cliente)" : "Opcional: audit trail"}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={`${MODAL_ACTIONS} flex-col-reverse sm:flex-row`}>
        <button
          onClick={() => setManualVerifyOrder(null)}
          disabled={busy}
          data-testid="manual-verify-cancel"
          className="flex-1 px-4 py-3 rounded-xl border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-sm font-bold disabled:opacity-40"
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={busy}
          data-testid="manual-verify-save"
          className={`flex-1 px-4 py-3 rounded-xl text-white text-sm font-black flex items-center justify-center gap-2 shadow-lg transition-all disabled:opacity-40 ${
            decision === "APPROVE"
              ? "bg-gradient-to-r from-green-600 to-emerald-500 hover:opacity-90"
              : "bg-gradient-to-r from-red-600 to-rose-500 hover:opacity-90"
          }`}
        >
          {busy ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" /> Guardando…
            </>
          ) : decision === "APPROVE" ? (
            <>
              <CheckCircle2 className="w-4 h-4" /> Guardar y Aprobar
            </>
          ) : (
            <>
              <XCircle className="w-4 h-4" /> Guardar y Rechazar
            </>
          )}
        </button>
      </div>
    </Modal>
  );
}
